/**
 * Poker AI Service Client
 * Communicates with the Python FastAPI service for AI decisions
 */

import { Card, Player } from '../types/holdem.types';

export type AISkillLevel = 'easy' | 'medium' | 'hard' | 'expert' | 'random' | 'pure-chaos';

interface GameStateForAPI {
    player_id: number;
    player_position: number;
    dealer_position: number;
    small_blind: number;
    big_blind: number;
    pot: number;
    current_bet: number;
    phase: 'pre-flop' | 'flop' | 'turn' | 'river';
    community_cards: APICard[];
    players: APIPlayer[];
}

interface APICard {
    rank: number; // 2-14 (11=J, 12=Q, 13=K, 14=A)
    suit: 'hearts' | 'diamonds' | 'clubs' | 'spades';
}

interface APIPlayer {
    id: number;
    name: string;
    chips: number;
    current_bet: number;
    is_active: boolean;
    is_folded: boolean;
    is_all_in: boolean;
    hole_cards: APICard[];
}

export interface AIDecisionResponse {
    action: 'fold' | 'check' | 'call' | 'raise' | 'all-in';
    raise_amount?: number;
    reasoning: string;
    confidence: number;
    usedFallback?: boolean; // Indicates if fallback logic was used instead of AI engine
}

export class PokerAIService {
    private baseURL: string;
    private randomSkillCache: Map<number, AISkillLevel> = new Map();

    constructor(baseURL?: string) {
        // Use relative URLs - nginx proxies /api/game/* to game server (port 3337)
        // Browser calls /api/game/ai-decision (same origin via nginx)
        // Nginx proxies to localhost:3337 (game server)
        // Game server then calls localhost:3335 (AI service) - never exposed publicly
        this.baseURL = baseURL || '';
    }

    /**
     * Get AI decision from the service
     */
    async getDecision(
        playerId: number,
        playerPosition: number,
        dealerPosition: number,
        smallBlind: number,
        bigBlind: number,
        pot: number,
        currentBet: number,
        phase: string,
        communityCards: Card[],
        players: Player[],
        skillLevel: AISkillLevel
    ): Promise<AIDecisionResponse> {
        // Handle special skill levels
        const actualSkillLevel = this.resolveSkillLevel(playerId, skillLevel);

        // Build game state
        const gameState: GameStateForAPI = {
            player_id: playerId,
            player_position: playerPosition,
            dealer_position: dealerPosition,
            small_blind: smallBlind,
            big_blind: bigBlind,
            pot: pot,
            current_bet: currentBet,
            phase: this.convertPhase(phase),
            community_cards: communityCards.map(card => this.convertCard(card)),
            players: players.map(player => this.convertPlayer(player, playerId))
        };

        try {
            // Call game server (which calls AI service server-side)
            // Browser → Vite proxy → Game Server (port 3334) → AI Service (port 3335, localhost only)
            const url = `${this.baseURL}/api/game/ai-decision`;
            console.log('[AI Service] Making request to game server:', url);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    gameState: gameState,
                    skillLevel: actualSkillLevel
                })
            });

            console.log('[AI Service] Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('[AI Service] Error response:', errorText);
                throw new Error(`API returned ${response.status}: ${errorText}`);
            }

            const decision = await response.json();
            console.log('[AI Service] SUCCESS - Decision:', decision);
            return decision;
        } catch (error) {
            console.error('[AI Service] FALLBACK - Error:', error);

            // NEVER fold when check is free!
            const player = players.find(p => p.id === playerId);
            if (player) {
                const callAmount = currentBet - player.currentBet;
                if (callAmount <= 0) {
                    console.log('[AI Service] FALLBACK: Check is free');
                    return {
                        action: 'check',
                        reasoning: 'Game server unavailable, checking',
                        confidence: 0.5,
                        usedFallback: true
                    };
                }
            }

            // Return a safe fallback decision
            console.log('[AI Service] FALLBACK: Returning fold');
            return {
                action: 'fold',
                reasoning: 'Game server unavailable',
                confidence: 0.5,
                usedFallback: true
            };
        }
    }

    /**
     * Resolve skill level for special modes
     */
    private resolveSkillLevel(playerId: number, skillLevel: AISkillLevel): 'easy' | 'medium' | 'hard' | 'expert' {
        const baseSkills: ('easy' | 'medium' | 'hard' | 'expert')[] = ['easy', 'medium', 'hard', 'expert'];

        if (skillLevel === 'random') {
            // Pick one level and cache it for this player
            if (!this.randomSkillCache.has(playerId)) {
                const randomSkill = baseSkills[Math.floor(Math.random() * baseSkills.length)];
                this.randomSkillCache.set(playerId, randomSkill);
            }
            return this.randomSkillCache.get(playerId) as 'easy' | 'medium' | 'hard' | 'expert';
        }

        if (skillLevel === 'pure-chaos') {
            // Pick a new random level for each action
            return baseSkills[Math.floor(Math.random() * baseSkills.length)];
        }

        return skillLevel;
    }

    /**
     * Clear cached random skill level for a player (call when they change skill level)
     */
    clearRandomSkillCache(playerId: number): void {
        this.randomSkillCache.delete(playerId);
    }

    /**
     * Convert card to API format
     */
    private convertCard(card: Card): APICard {
        return {
            rank: card.rank,
            suit: card.suit as 'hearts' | 'diamonds' | 'clubs' | 'spades'
        };
    }

    /**
     * Convert player to API format
     */
    private convertPlayer(player: Player, aiPlayerId: number): APIPlayer {
        return {
            id: player.id,
            name: player.name,
            chips: player.chips,
            current_bet: player.currentBet,
            is_active: player.isActive,
            is_folded: player.isFolded,
            is_all_in: player.isAllIn,
            // Only include hole cards for the AI player making the decision
            hole_cards: player.id === aiPlayerId ? player.holeCards.map(c => this.convertCard(c)) : []
        };
    }

    /**
     * Convert phase to API format
     */
    private convertPhase(phase: string): 'pre-flop' | 'flop' | 'turn' | 'river' {
        if (phase === 'pre-flop') return 'pre-flop';
        if (phase === 'flop') return 'flop';
        if (phase === 'turn') return 'turn';
        if (phase === 'river') return 'river';
        return 'pre-flop'; // Default fallback
    }

    /**
     * Test connection to game server (which tests AI service connection)
     */
    async testConnection(): Promise<boolean> {
        try {
            const response = await fetch(`${this.baseURL}/api/game/ai-status`);
            if (response.ok) {
                const data = await response.json();
                return data.connected;
            }
            return false;
        } catch (error) {
            return false;
        }
    }
}
